/**
 * Cost Centers Tab - Centros de Custos & Receitas
 * Organizado por grandes grupos (Marketing, Comercial, Tech, etc.)
 * com sub-contas internas e totalizacao por grupo e por conta.
 *
 * v2 (migration 312): Carrega dados reais de financial_expenses via
 * GET /governance/finance/planning/expenses/cost-center-actuals
 * e os sobrepõe nas células quando disponíveis (badge "real" laranja).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, ChevronDown, ChevronRight, RefreshCw, Check, X, Database } from 'lucide-react';
import { AZURE } from '../../services/api';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const formatCurrency = (v) => {
  if (v == null || v === 0) return 'R$ 0';
  return 'R$ ' + Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Default structure for initial seeding
const DEFAULT_GROUPS = [
  {
    code: 'receita',
    name: 'Receita',
    type: 'revenue',
    accounts: [
      { code: 'rec_swimvision', name: 'SwimVision (SaaS)', type: 'revenue' },
      { code: 'rec_trackvision', name: 'TrackVision (SaaS)', type: 'revenue' },
      { code: 'rec_consultoria', name: 'Consultoria & Projetos', type: 'revenue' },
      { code: 'rec_licenciamento', name: 'Licenciamento IP', type: 'revenue' },
      { code: 'rec_outros', name: 'Outras Receitas', type: 'revenue' },
    ]
  },
  {
    code: 'marketing',
    name: 'Marketing',
    type: 'cost',
    accounts: [
      { code: 'mkt_ads', name: 'Anuncios Digitais (Google/Meta)', type: 'cost' },
      { code: 'mkt_content', name: 'Producao de Conteudo', type: 'cost' },
      { code: 'mkt_eventos', name: 'Eventos & Patrocinios', type: 'cost' },
      { code: 'mkt_ferramentas', name: 'Ferramentas (HubSpot, etc)', type: 'cost' },
      { code: 'mkt_branding', name: 'Branding & Design', type: 'cost' },
    ]
  },
  {
    code: 'comercial',
    name: 'Comercial',
    type: 'cost',
    accounts: [
      { code: 'com_salarios', name: 'Salarios & Comissoes', type: 'cost' },
      { code: 'com_viagens', name: 'Viagens & Deslocamento', type: 'cost' },
      { code: 'com_crm', name: 'CRM & Ferramentas', type: 'cost' },
      { code: 'com_representantes', name: 'Representantes Externos', type: 'cost' },
    ]
  },
  {
    code: 'tech',
    name: 'Tecnologia & Produto',
    type: 'cost',
    accounts: [
      { code: 'tech_cloud', name: 'Azure (Infra Cloud)', type: 'cost' },
      { code: 'tech_ai', name: 'Azure AI (OpenAI, Foundry)', type: 'cost' },
      { code: 'tech_devs', name: 'Desenvolvedores (Salarios)', type: 'cost' },
      { code: 'tech_ferramentas', name: 'Ferramentas Dev (GitHub, etc)', type: 'cost' },
      { code: 'tech_dados', name: 'Banco de Dados & Storage', type: 'cost' },
      { code: 'tech_dominio', name: 'Dominios & Certificados', type: 'cost' },
    ]
  },
  {
    code: 'operacional',
    name: 'Operacional & Admin',
    type: 'cost',
    accounts: [
      { code: 'ops_contabilidade', name: 'Contabilidade & Fiscal', type: 'cost' },
      { code: 'ops_juridico', name: 'Juridico', type: 'cost' },
      { code: 'ops_escritorio', name: 'Escritorio & Coworking', type: 'cost' },
      { code: 'ops_seguros', name: 'Seguros', type: 'cost' },
      { code: 'ops_bancarias', name: 'Tarifas Bancarias', type: 'cost' },
      { code: 'ops_impostos', name: 'Impostos & Tributos', type: 'cost' },
    ]
  },
  {
    code: 'pessoas',
    name: 'Pessoas & RH',
    type: 'cost',
    accounts: [
      { code: 'rh_founders', name: 'Pro-labore Socios', type: 'cost' },
      { code: 'rh_beneficios', name: 'Beneficios (VR, Saude)', type: 'cost' },
      { code: 'rh_treinamento', name: 'Treinamento & Cursos', type: 'cost' },
      { code: 'rh_freelancers', name: 'Freelancers & Terceiros', type: 'cost' },
    ]
  },
];

const CostCentersTab = () => {
  const [groups, setGroups] = useState(DEFAULT_GROUPS);
  const [entries, setEntries] = useState({}); // { accountCode: { jan: 1000, ... } } — valores manuais (orçamento)
  const [actuals, setActuals] = useState({}); // { accountCode: { jan: 850, ... } } — dados reais de financial_expenses
  const [actualsYear, setActualsYear] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(['receita', 'marketing', 'comercial', 'tech']);
  const [loading, setLoading] = useState(true);
  const [periodView, setPeriodView] = useState('monthly'); // monthly, quarterly
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [addingAccount, setAddingAccount] = useState(null); // groupCode
  const [newAccountName, setNewAccountName] = useState('');
  const [showActuals, setShowActuals] = useState(true); // toggle overlay real data

  const MONTHS = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
  const MONTH_LABELS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const QUARTERS = [
    { label: 'Q1', months: ['jan','fev','mar'] },
    { label: 'Q2', months: ['abr','mai','jun'] },
    { label: 'Q3', months: ['jul','ago','set'] },
    { label: 'Q4', months: ['out','nov','dez'] },
  ];

  // Load data from backend
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Carregar entradas manuais (orçamento)
      const { data } = await AZURE.get('/governance/finance/cost-centers');
      if (data?.groups) setGroups(data.groups);
      if (data?.entries) setEntries(data.entries);
    } catch (e) {
      // Se endpoint não existe, usa defaults com entradas vazias
      console.warn('Cost centers endpoint not available, using defaults');
      const emptyEntries = {};
      DEFAULT_GROUPS.forEach(g => g.accounts.forEach(a => {
        emptyEntries[a.code] = {};
        MONTHS.forEach(m => { emptyEntries[a.code][m] = 0; });
      }));
      setEntries(emptyEntries);
    }

    // 2. Carregar dados reais de financial_expenses (migration 312)
    try {
      const currentYear = new Date().getFullYear();
      const { data: actData } = await AZURE.get(`/governance/finance/planning/expenses/cost-center-actuals?year=${currentYear}`);
      if (actData?.data?.actuals) {
        setActuals(actData.data.actuals);
        setActualsYear(actData.data.year);
      }
    } catch (e) {
      // Silencioso — migration 312 pode não estar aplicada ainda
      setActuals({});
    }

    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleGroup = (code) => {
    setExpandedGroups(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const getAccountTotal = (accountCode) => {
    const acc = entries[accountCode] || {};
    return MONTHS.reduce((sum, m) => sum + (acc[m] || 0), 0);
  };

  // Valor real (financial_expenses) para uma conta × mês
  const getActualValue = (accountCode, month) => {
    return (actuals[accountCode] || {})[month] || 0;
  };

  // Valor a exibir: real (se disponível e toggle ativo) ou orçamento manual
  const getDisplayValue = (accountCode, month) => {
    if (showActuals) {
      const real = getActualValue(accountCode, month);
      if (real > 0) return real;
    }
    return (entries[accountCode] || {})[month] || 0;
  };

  const hasActual = (accountCode, month) =>
    showActuals && getActualValue(accountCode, month) > 0;

  const getGroupTotal = (group) => {
    return group.accounts.reduce((sum, a) => sum + getAccountTotal(a.code), 0);
  };

  const getGroupMonthTotal = (group, month) => {
    return group.accounts.reduce((sum, a) => sum + getDisplayValue(a.code, month), 0);
  };

  const getGroupQuarterTotal = (group, quarter) => {
    return quarter.months.reduce((sum, m) => sum + getGroupMonthTotal(group, m), 0);
  };

  const getAccountQuarterTotal = (accountCode, quarter) => {
    return quarter.months.reduce((sum, m) => sum + getDisplayValue(accountCode, m), 0);
  };

  const getTotalRevenue = () => groups.filter(g => g.type === 'revenue').reduce((s, g) => s + getGroupTotal(g), 0);
  const getTotalCosts = () => groups.filter(g => g.type === 'cost').reduce((s, g) => s + getGroupTotal(g), 0);

  const handleCellEdit = (accountCode, month) => {
    setEditingCell(`${accountCode}-${month}`);
    setEditValue(String((entries[accountCode] || {})[month] || 0));
  };

  const handleCellSave = async (accountCode, month) => {
    const val = parseFloat(editValue) || 0;
    setEntries(prev => ({
      ...prev,
      [accountCode]: { ...(prev[accountCode] || {}), [month]: val }
    }));
    setEditingCell(null);
    // Persist to backend
    try {
      await AZURE.put('/governance/finance/cost-centers/entry', {
        account_code: accountCode, month, value: val
      });
    } catch (e) { console.warn('Save failed, data kept locally'); }
  };

  const handleAddAccount = (groupCode) => {
    if (!newAccountName.trim()) return;
    const code = groupCode + '_' + newAccountName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 20);
    setGroups(prev => prev.map(g => {
      if (g.code !== groupCode) return g;
      return { ...g, accounts: [...g.accounts, { code, name: newAccountName.trim(), type: g.type }] };
    }));
    setEntries(prev => {
      const acc = {};
      MONTHS.forEach(m => { acc[m] = 0; });
      return { ...prev, [code]: acc };
    });
    setNewAccountName('');
    setAddingAccount(null);
  };

  const columns = periodView === 'monthly' ? MONTHS : QUARTERS.map(q => q.label);

  if (loading) return <div className="h-96 bg-white rounded-xl animate-pulse" />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Building2 size={18} style={{ color: BRAND_GREEN }} />
          <h2 className="text-lg font-bold text-gray-900">Centros de Custos & Receitas</h2>
          {actualsYear && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
              <Database size={9} /> Real {actualsYear}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {actualsYear && (
            <button onClick={() => setShowActuals(v => !v)}
              className={`text-xs px-2 py-1 border rounded font-semibold transition-colors ${showActuals ? 'bg-orange-100 border-orange-300 text-orange-700' : 'bg-gray-100 border-gray-200 text-gray-500'}`}>
              {showActuals ? '● Real' : '○ Orçamento'}
            </button>
          )}
          <select value={periodView} onChange={e => setPeriodView(e.target.value)}
            className="text-xs border rounded px-2 py-1 font-semibold">
            <option value="monthly">Mensal</option>
            <option value="quarterly">Trimestral</option>
          </select>
          <button onClick={load} className="text-xs px-2 py-1 border rounded hover:bg-gray-100">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Receita Total (12m)</div>
          <div className="text-2xl font-bold text-green-700">{formatCurrency(getTotalRevenue())}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Custos Total (12m)</div>
          <div className="text-2xl font-bold text-red-700">{formatCurrency(getTotalCosts())}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Resultado (12m)</div>
          <div className={'text-2xl font-bold ' + (getTotalRevenue() - getTotalCosts() >= 0 ? 'text-green-700' : 'text-red-700')}>
            {getTotalRevenue() - getTotalCosts() >= 0 ? '+' : '-'}{formatCurrency(Math.abs(getTotalRevenue() - getTotalCosts()))}
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left font-bold text-gray-600 min-w-[220px] sticky left-0 bg-gray-50">Centro / Conta</th>
                {periodView === 'monthly' ? (
                  MONTH_LABELS.map(m => <th key={m} className="px-2 py-2 text-right font-bold text-gray-600 min-w-[80px]">{m}</th>)
                ) : (
                  QUARTERS.map(q => <th key={q.label} className="px-2 py-2 text-right font-bold text-gray-600 min-w-[100px]">{q.label}</th>)
                )}
                <th className="px-3 py-2 text-right font-bold text-gray-900 min-w-[100px] bg-gray-100">Total</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(group => {
                const isExpanded = expandedGroups.includes(group.code);
                const groupTotal = getGroupTotal(group);
                const isRevenue = group.type === 'revenue';
                return (
                  <React.Fragment key={group.code}>
                    {/* Group Header Row */}
                    <tr className={'border-b cursor-pointer hover:bg-gray-50 ' + (isRevenue ? 'bg-green-50/50' : 'bg-red-50/30')}
                      onClick={() => toggleGroup(group.code)}>
                      <td className="px-3 py-2.5 font-bold text-gray-900 sticky left-0" style={{ backgroundColor: isRevenue ? '#f0fdf4' : '#fef2f2' }}>
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isRevenue ? '#16a34a' : '#dc2626' }} />
                          {group.name}
                          <span className="text-[10px] text-gray-400 font-normal ml-1">({group.accounts.length} contas)</span>
                        </div>
                      </td>
                      {periodView === 'monthly' ? (
                        MONTHS.map(m => (
                          <td key={m} className={'px-2 py-2.5 text-right font-bold ' + (isRevenue ? 'text-green-700' : 'text-red-700')}>
                            {formatCurrency(getGroupMonthTotal(group, m))}
                          </td>
                        ))
                      ) : (
                        QUARTERS.map(q => (
                          <td key={q.label} className={'px-2 py-2.5 text-right font-bold ' + (isRevenue ? 'text-green-700' : 'text-red-700')}>
                            {formatCurrency(getGroupQuarterTotal(group, q))}
                          </td>
                        ))
                      )}
                      <td className={'px-3 py-2.5 text-right font-bold bg-gray-100 ' + (isRevenue ? 'text-green-800' : 'text-red-800')}>
                        {formatCurrency(groupTotal)}
                      </td>
                    </tr>

                    {/* Account Rows */}
                    {isExpanded && group.accounts.map(account => (
                      <tr key={account.code} className="border-b hover:bg-blue-50/20">
                        <td className="px-3 py-1.5 pl-10 text-gray-700 sticky left-0 bg-white">
                          {account.name}
                        </td>
                        {periodView === 'monthly' ? (
                          MONTHS.map(m => {
                            const cellKey = `${account.code}-${m}`;
                            const val = getDisplayValue(account.code, m);
                            const isReal = hasActual(account.code, m);
                            return (
                              <td key={m} className={'px-2 py-1.5 text-right font-mono cursor-pointer hover:bg-yellow-50' + (isReal ? ' bg-orange-50/40' : '')}
                                onClick={() => handleCellEdit(account.code, m)}>
                                {editingCell === cellKey ? (
                                  <input value={editValue} onChange={e => setEditValue(e.target.value)}
                                    onBlur={() => handleCellSave(account.code, m)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') handleCellSave(account.code, m);
                                      if (e.key === 'Escape') setEditingCell(null);
                                    }}
                                    className="w-16 text-right border rounded px-1 text-xs" autoFocus />
                                ) : (
                                  <span className={
                                    val === 0 ? 'text-gray-300' :
                                    isReal ? 'text-orange-600 font-semibold' :
                                    (isRevenue ? 'text-green-600' : 'text-red-600')
                                  }>
                                    {val === 0 ? '-' : formatCurrency(val)}
                                    {isReal && <span className="text-[8px] ml-0.5 text-orange-400">●</span>}
                                  </span>
                                )}
                              </td>
                            );
                          })
                        ) : (
                          QUARTERS.map(q => (
                            <td key={q.label} className="px-2 py-1.5 text-right font-mono">
                              <span className={getAccountQuarterTotal(account.code, q) === 0 ? 'text-gray-300' : (isRevenue ? 'text-green-600' : 'text-red-600')}>
                                {getAccountQuarterTotal(account.code, q) === 0 ? '-' : formatCurrency(getAccountQuarterTotal(account.code, q))}
                              </span>
                            </td>
                          ))
                        )}
                        <td className="px-3 py-1.5 text-right font-mono font-semibold bg-gray-50">
                          <span className={getAccountTotal(account.code) === 0 ? 'text-gray-300' : (isRevenue ? 'text-green-700' : 'text-red-700')}>
                            {getAccountTotal(account.code) === 0 ? '-' : formatCurrency(getAccountTotal(account.code))}
                          </span>
                        </td>
                      </tr>
                    ))}

                    {/* Add account row */}
                    {isExpanded && (
                      <tr className="border-b">
                        <td className="px-3 py-1 pl-10 sticky left-0 bg-white" colSpan={periodView === 'monthly' ? 14 : 6}>
                          {addingAccount === group.code ? (
                            <div className="flex items-center gap-2">
                              <input value={newAccountName} onChange={e => setNewAccountName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddAccount(group.code); if (e.key === 'Escape') setAddingAccount(null); }}
                                placeholder="Nome da conta..." className="border rounded px-2 py-0.5 text-xs w-48" autoFocus />
                              <button onClick={() => handleAddAccount(group.code)} className="text-green-600 hover:text-green-800"><Check size={12} /></button>
                              <button onClick={() => setAddingAccount(null)} className="text-gray-400 hover:text-gray-600"><X size={12} /></button>
                            </div>
                          ) : (
                            <button onClick={() => setAddingAccount(group.code)} className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600">
                              <Plus size={10} /> Adicionar conta
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Grand Total Row */}
              <tr className="border-t-2 bg-gray-100">
                <td className="px-3 py-3 font-bold text-gray-900 sticky left-0 bg-gray-100">RESULTADO LIQUIDO</td>
                {periodView === 'monthly' ? (
                  MONTHS.map(m => {
                    const rev = groups.filter(g => g.type === 'revenue').reduce((s, g) => s + getGroupMonthTotal(g, m), 0);
                    const cost = groups.filter(g => g.type === 'cost').reduce((s, g) => s + getGroupMonthTotal(g, m), 0);
                    const net = rev - cost;
                    return (
                      <td key={m} className={'px-2 py-3 text-right font-bold ' + (net >= 0 ? 'text-green-800' : 'text-red-800')}>
                        {net >= 0 ? '+' : ''}{formatCurrency(net)}
                      </td>
                    );
                  })
                ) : (
                  QUARTERS.map(q => {
                    const rev = groups.filter(g => g.type === 'revenue').reduce((s, g) => s + getGroupQuarterTotal(g, q), 0);
                    const cost = groups.filter(g => g.type === 'cost').reduce((s, g) => s + getGroupQuarterTotal(g, q), 0);
                    const net = rev - cost;
                    return (
                      <td key={q.label} className={'px-2 py-3 text-right font-bold ' + (net >= 0 ? 'text-green-800' : 'text-red-800')}>
                        {net >= 0 ? '+' : ''}{formatCurrency(net)}
                      </td>
                    );
                  })
                )}
                <td className={'px-3 py-3 text-right font-bold text-lg ' + (getTotalRevenue() - getTotalCosts() >= 0 ? 'text-green-800' : 'text-red-800')}>
                  {getTotalRevenue() - getTotalCosts() >= 0 ? '+' : ''}{formatCurrency(getTotalRevenue() - getTotalCosts())}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="text-[10px] text-gray-400 text-right space-y-0.5">
        <div>Clique nas celulas para editar valores de orçamento. Grupos expandem/colapsam ao clicar. Use + para adicionar contas.</div>
        {actualsYear && showActuals && (
          <div className="text-orange-400">
            ● = valor real de <code>financial_expenses</code> ({actualsYear}). Clique para sobrescrever com orçamento manual.
          </div>
        )}
      </div>
    </div>
  );
};

export default CostCentersTab;
